import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Bot, UserCheck, RefreshCw, Search, Send, Download, Paperclip, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n';

function MidiaBolha({ msg }) {
  const { t } = useT();
  const [src, setSrc] = useState(null);
  const [carregando, setCarregando] = useState(false);

  // Revoga URL de objeto ao desmontar para evitar memory leak
  useEffect(() => {
    return () => { if (src) URL.revokeObjectURL(src); };
  }, [src]);

  async function carregar() {
    if (src || carregando) return;
    setCarregando(true);
    try {
      const resp = await api.get(`/ia/mensagens/${msg.id}/midia`, { responseType: 'blob' });
      setSrc(URL.createObjectURL(resp.data));
    } catch (err) {
      const status = err.response?.status;
      if (status === 404 || status === 502) {
        toast.error(t('conversas.toast_midia_expirada'));
      } else {
        toast.error(t('conversas.toast_midia_erro'));
      }
    } finally {
      setCarregando(false);
    }
  }

  const legenda = msg.texto && msg.texto !== `[${msg.tipo}]` ? msg.texto : null;

  if (msg.tipo === 'imagem') {
    return (
      <div>
        {src
          ? <img src={src} alt="imagem" onClick={() => window.open(src, '_blank')}
              style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, cursor: 'pointer', display: 'block' }} />
          : <button onClick={carregar} disabled={carregando}
              style={{ background: 'rgba(0,0,0,0.15)', border: 'none', borderRadius: 8, padding: '32px 40px', cursor: 'pointer', color: 'inherit', fontSize: 28 }}>
              {carregando ? '⏳' : '🖼'}
            </button>}
        {legenda && <div style={{ fontSize: 13, marginTop: 4 }}>{legenda}</div>}
      </div>
    );
  }

  if (msg.tipo === 'audio') {
    return (
      <div>
        {src
          ? <audio controls src={src} style={{ maxWidth: 220 }} />
          : <button onClick={carregar} disabled={carregando}
              style={{ background: 'rgba(0,0,0,0.15)', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', color: 'inherit', display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
              {carregando ? '⏳' : '🎤'} {carregando ? t('conversas.anexo_carregando') : t('conversas.audio_ouvir')}
            </button>}
      </div>
    );
  }

  if (msg.tipo === 'video') {
    return (
      <div>
        {src
          ? <video controls src={src} style={{ maxWidth: 220, maxHeight: 180, borderRadius: 8 }} />
          : <button onClick={carregar} disabled={carregando}
              style={{ background: 'rgba(0,0,0,0.15)', border: 'none', borderRadius: 8, padding: '32px 40px', cursor: 'pointer', color: 'inherit', fontSize: 28 }}>
              {carregando ? '⏳' : '🎥'}
            </button>}
        {legenda && <div style={{ fontSize: 13, marginTop: 4 }}>{legenda}</div>}
      </div>
    );
  }

  if (msg.tipo === 'documento') {
    const nomeArq = legenda || 'documento';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 24 }}>📄</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{nomeArq}</div>
          {src
            ? <a href={src} download={nomeArq} style={{ fontSize: 12, color: 'inherit', opacity: 0.8, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Download size={12} /> {t('conversas.anexo_baixar')}
              </a>
            : <button onClick={carregar} disabled={carregando}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 12, opacity: 0.8, padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Download size={12} /> {carregando ? t('conversas.anexo_carregando') : t('conversas.anexo_baixar')}
              </button>}
        </div>
      </div>
    );
  }

  if (msg.tipo === 'sticker') return <span style={{ fontSize: 40 }}>😊</span>;

  return <span>{msg.texto}</span>;
}

const C = {
  bg: '#f7f8fa',
  card: '#ffffff',
  border: '#d0d5dd',
  borderForte: '#98a2b3',
  texto: '#101828',
  textoSecundario: '#475467',
  textoMudo: '#98a2b3',
  hover: '#f2f4f7',
  ativo: '#f0fdf4',
  primaria: '#13305f',
  ok: '#16a34a',
  okBg: '#dcfce7',
  alerta: '#b91c1c',
  alertaBg: '#fee2e2',
  bolhaMim: '#13305f',
  bolhaOutro: '#f2f4f7'
};

function formatarHora(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const hoje = new Date();
  const mesmo = d.toDateString() === hoje.toDateString();
  return mesmo
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

const pausadaAgora = (ts) => ts && new Date(ts) > new Date();

function corDaInstancia(nome) {
  const cores = ['#13305f', '#16a34a', '#ea580c', '#13305f', '#0891b2', '#db2777', '#65a30d', '#dc2626'];
  let h = 0;
  for (let i = 0; i < (nome || '').length; i++) h = (h * 31 + nome.charCodeAt(i)) >>> 0;
  return cores[h % cores.length];
}

export default function Conversas() {
  const { isSuperadmin, quotaIa } = useAuth();
  const { t } = useT();
  const [conversas, setConversas] = useState([]);
  const [quotaEsgotada, setQuotaEsgotada] = useState(false);
  const [filtroQualif, setFiltroQualif] = useState('__todas__');
  const [selecionadaId, setSelecionadaId] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [carregandoMsgs, setCarregandoMsgs] = useState(false);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [anexo, setAnexo] = useState(null);
  const inputArquivoRef = useRef(null);
  const fimRef = useRef(null);
  const erroConversasRef = useRef(false); // suprime toasts repetidos no polling

  const carregarConversas = useCallback(async () => {
    try {
      const { data } = await api.get('/ia/conversas');
      erroConversasRef.current = false;
      const lista = Array.isArray(data) ? data : (data?.conversas ?? []);
      setConversas(lista);
      setQuotaEsgotada(data?.quotaEsgotada === true);
    } catch {
      if (!erroConversasRef.current) {
        erroConversasRef.current = true;
        toast.error(t('conversas.toast_erro_conversas'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  const [debugOpen, setDebugOpen] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [debugWebhook, setDebugWebhook] = useState(null);
  const [debugDiag, setDebugDiag] = useState(null);
  const [reativandoTodas, setReativandoTodas] = useState(false);
  const [reconfigurandoWebhook, setReconfigurandoWebhook] = useState(false);

  // Detecta se o webhook na Evolution está sem o header x-webhook-secret.
  // Quando ausente, o backend rejeita os POSTs do Evolution com 401 — webhooks
  // nunca chegam ao ring buffer e a IA nunca recebe a mensagem.
  function webhookSemSecret(wh) {
    if (!wh || wh.erro) return false;
    const hdrs = wh.headers || wh.webhook?.headers;
    if (!hdrs) return true;
    if (typeof hdrs === 'object' && !Array.isArray(hdrs)) {
      return !Object.keys(hdrs).some(k => k.toLowerCase() === 'x-webhook-secret');
    }
    return true;
  }

  async function reconfigurarWebhook() {
    const inst = conversas.find(c => c.id === selecionadaId)?.instancia;
    if (!inst) { toast.error('Selecione uma conversa primeiro'); return; }
    setReconfigurandoWebhook(true);
    try {
      await api.post(`/whatsapp/instancias/${encodeURIComponent(inst)}/configurar-webhook`);
      toast.success(`Webhook reconfigurado para ${inst}. Mensagens devem chegar agora.`);
      const { data } = await api.get(`/whatsapp/instancias/${encodeURIComponent(inst)}/webhook`).catch(() => ({ data: null }));
      setDebugWebhook(data);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao reconfigurar webhook');
    } finally {
      setReconfigurandoWebhook(false);
    }
  }

  async function reconfigurarTodosWebhooks() {
    setReconfigurandoWebhook(true);
    try {
      const { data } = await api.post('/whatsapp/webhooks/re-registrar-tenant');
      if (data.falhas > 0) {
        toast.error(`${data.sucesso}/${data.total} webhooks reconfigurados. ${data.falhas} falharam.`);
      } else {
        toast.success(`${data.sucesso} webhook(s) reconfigurado(s). Mensagens devem chegar agora.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao reconfigurar webhooks');
    } finally {
      setReconfigurandoWebhook(false);
    }
  }

  async function abrirDebug() {
    setDebugOpen(true);
    try {
      const { data } = await api.get('/webhook/evolution/ultimos');
      setDebugLogs(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(t('conversas.toast_webhook_err'));
    }
    const inst = conversas.find(c => c.id === selecionadaId)?.instancia;
    if (inst) {
      try {
        const [wh, diag] = await Promise.all([
          api.get(`/whatsapp/instancias/${encodeURIComponent(inst)}/webhook`).catch(() => ({ data: null })),
          api.get(`/ia/diagnostico/${encodeURIComponent(inst)}`).catch(() => ({ data: null }))
        ]);
        setDebugWebhook(wh.data);
        setDebugDiag(diag.data);
      } catch {
        setDebugWebhook({ erro: t('conversas.toast_webhook_err') });
        setDebugDiag(null);
      }
    } else {
      setDebugWebhook(null);
      setDebugDiag(null);
    }
  }

  async function reativarTodasConversas() {
    const inst = conversas.find(c => c.id === selecionadaId)?.instancia
      || [...new Set(conversas.map(c => c.instancia))][0];
    if (!inst) { toast.error('Selecione uma conversa primeiro'); return; }
    setReativandoTodas(true);
    try {
      const { data } = await api.post(`/ia/instancias/${encodeURIComponent(inst)}/reativar-todas`);
      toast.success(`IA reativada em ${data.afetadas} conversa(s) da instância ${inst}`);
      await carregarConversas();
      if (debugOpen) {
        const diag = await api.get(`/ia/diagnostico/${encodeURIComponent(inst)}`).catch(() => ({ data: null }));
        setDebugDiag(diag.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao reativar IA');
    } finally {
      setReativandoTodas(false);
    }
  }

  const carregarMensagens = useCallback(async (id) => {
    if (!id) return;
    setCarregandoMsgs(true);
    try {
      const { data } = await api.get(`/ia/conversas/${id}/mensagens`);
      setMensagens(data);
    } catch {
      toast.error(t('conversas.toast_erro_msgs'));
    } finally {
      setCarregandoMsgs(false);
    }
  }, [t]);

  useEffect(() => {
    carregarConversas();
  }, [carregarConversas]);

  useEffect(() => {
    const interval = setInterval(() => {
      carregarConversas();
      if (selecionadaId) carregarMensagens(selecionadaId);
    }, 8000);
    return () => clearInterval(interval);
  }, [selecionadaId, carregarConversas, carregarMensagens]);

  useEffect(() => {
    if (selecionadaId) carregarMensagens(selecionadaId);
  }, [selecionadaId, carregarMensagens]);

  // Só rola para o fim quando chega uma mensagem nova (último ID muda),
  // não em todo re-render do polling para não interromper scroll de leitura
  const ultimaMsgId = useMemo(() => mensagens[mensagens.length - 1]?.id, [mensagens]);
  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ultimaMsgId]);

  const conversa = conversas.find(c => c.id === selecionadaId);

  // Categoria de qualificação do lead, derivada de contexto_ia.etapa
  const categoriaConversa = (c) => {
    const etapa = c.contexto_ia && typeof c.contexto_ia === 'object' ? c.contexto_ia.etapa : null;
    if (etapa === 'agendado') return 'agendados';
    if (etapa === 'aguardando_horario' || etapa === 'qualificacao' || c.pendente_equipe) return 'aguardando';
    return 'outros';
  };

  const contadorCategoria = conversas.reduce((acc, c) => {
    const cat = categoriaConversa(c);
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});

  const filtradas = conversas.filter(c => {
    if (filtroQualif !== '__todas__' && categoriaConversa(c) !== filtroQualif) return false;
    if (!busca) return true;
    const q = busca.toLowerCase();
    return (c.nome_contato || '').toLowerCase().includes(q) || (c.telefone || '').includes(busca);
  });

  async function assumir() {
    if (!conversa) return;
    try {
      const { data } = await api.post(`/ia/conversas/${conversa.id}/assumir`, {});
      toast.success(t('conversas.toast_pausada', { horas: data.horas }));
      carregarConversas();
    } catch { toast.error(t('conversas.toast_err_pausar')); }
  }

  async function retomar() {
    if (!conversa) return;
    try {
      await api.post(`/ia/conversas/${conversa.id}/retomar`);
      toast.success(t('conversas.toast_retomada'));
      carregarConversas();
    } catch { toast.error(t('conversas.toast_err_retomar')); }
  }

  async function reativarIA() {
    if (!conversa) return;
    try {
      await api.post(`/ia/conversas/${conversa.id}/reativar`);
      toast.success(t('conversas.toast_reativada'));
      await carregarConversas();
      carregarMensagens(conversa.id);
    } catch { toast.error(t('conversas.toast_err_reativar')); }
  }

  async function toggleIAGrupo() {
    if (!conversa) return;
    const novoEstado = !conversa.ia_grupo_assumida;
    try {
      await api.post(`/ia/conversas/${conversa.id}/assumir-grupo`, { assumir: novoEstado });
      toast.success(novoEstado ? t('conversas.toast_ia_grupo_ativada') : t('conversas.toast_ia_grupo_desativada'));
      await carregarConversas();
    } catch { toast.error(t('conversas.toast_err_ia_grupo')); }
  }

  function selecionarArquivo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 16 * 1024 * 1024) { toast.error(t('conversas.arquivo_grande')); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setAnexo({
        base64: dataUrl,
        mimetype: file.type,
        fileName: file.name,
        previewUrl: file.type.startsWith('image/') ? dataUrl : null
      });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function enviar() {
    if (!conversa || enviando) return;
    setEnviando(true);
    try {
      if (anexo) {
        await api.post(`/ia/conversas/${conversa.id}/enviar-midia`, {
          base64: anexo.base64,
          mimetype: anexo.mimetype,
          fileName: anexo.fileName,
          caption: texto.trim()
        });
        setAnexo(null);
      } else {
        const trimmed = texto.trim();
        if (!trimmed) { setEnviando(false); return; }
        await api.post(`/ia/conversas/${conversa.id}/enviar`, { texto: trimmed });
      }
      setTexto('');
      await carregarMensagens(conversa.id);
      await carregarConversas();
    } catch (err) {
      toast.error(err.response?.data?.erro || t('conversas.toast_err_enviar'));
    } finally {
      setEnviando(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey && !anexo) {
      e.preventDefault();
      enviar();
    }
  }

  const inputBase = {
    width: '100%', padding: '8px 12px',
    background: C.card, color: C.texto,
    border: `1px solid ${C.border}`, borderRadius: 6,
    fontSize: 14, outline: 'none'
  };

  const semAcessoIA = !isSuperadmin && quotaIa && !quotaIa.ilimitado && quotaIa.limite === 0 && !quotaIa.creditosIA;

  return (
    <div className="page" style={{ color: C.texto }}>
      {semAcessoIA && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          background: 'rgba(139,92,246,0.07)', border: '1px solid rgba(139,92,246,0.3)',
          borderRadius: 10, padding: '12px 16px', marginBottom: 16,
        }}>
          <Bot size={18} color="#13305f" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ margin: 0, fontSize: 13, color: '#b9bdd1', lineHeight: 1.6 }}>
            <strong style={{ color: '#4ade80', fontWeight: 600 }}>Concierge IA sem créditos — </strong>
            {t('conversas.aviso_sem_ia', 'As conversas ficam visíveis, mas a IA não responderá automaticamente.')}
            {' '}
            <Link to="/creditos" style={{ color: '#13305f', fontWeight: 600 }}>
              {t('conversas.aviso_sem_ia_link', 'Adquirir créditos →')}
            </Link>
          </p>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <h1 className="page-h" style={{ marginBottom: 4, color: C.texto }}>
            {t('conversas.titulo')} <span className="sidebar-badge" style={{ marginLeft: 8 }}>BETA · ADMIN</span>
          </h1>
          <p className="page-h-sub" style={{ color: C.textoSecundario }}>
            {t('conversas.subtitulo')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={abrirDebug}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
              background: C.card, border: `1px solid ${C.border}`, color: C.texto, fontSize: 14
            }}
          >
            {t('conversas.btn_debug')}
          </button>
          <button
            onClick={carregarConversas}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
              background: C.card, border: `1px solid ${C.border}`, color: C.texto, fontSize: 14
            }}
          >
            <RefreshCw size={14} /> {t('conversas.btn_atualizar')}
          </button>
        </div>
      </div>

      {debugOpen && (
        <div onClick={() => setDebugOpen(false)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: C.card, borderRadius: 8, maxWidth: 800, width: '100%',
            maxHeight: '85vh', overflow: 'auto', padding: 20
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 600 }}>{t('conversas.debug_titulo')}</h2>
              <button onClick={() => setDebugOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer' }}>×</button>
            </div>

            {debugDiag && (
              <div style={{ marginBottom: 16, padding: 12, borderRadius: 8,
                background: debugDiag.ok ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${debugDiag.ok ? '#bbf7d0' : '#fecaca'}` }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6,
                  color: debugDiag.ok ? '#15803d' : '#b91c1c' }}>
                  {debugDiag.ok ? '✅ IA configurada e pronta para responder' : '❌ Problemas encontrados:'}
                </div>
                {debugDiag.problemas?.length > 0 && (
                  <ul style={{ margin: '0 0 8px', paddingLeft: 20, fontSize: 12 }}>
                    {debugDiag.problemas.map((p, i) => <li key={i} style={{ marginBottom: 4, color: '#b91c1c' }}>{p}</li>)}
                  </ul>
                )}
                {(debugDiag.checks?.conversas_pausadas > 0 || debugDiag.checks?.conversas_encerradas > 0) && (
                  <button
                    onClick={() => { setDebugOpen(false); reativarTodasConversas(); }}
                    style={{ padding: '6px 14px', borderRadius: 6, cursor: 'pointer',
                      background: '#dc2626', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                    Reativar IA para todas as conversas
                  </button>
                )}
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                {t('conversas.debug_config_titulo')} {conversa?.instancia && `(${conversa.instancia})`}
              </h3>
              {!conversa?.instancia ? (
                <p style={{ fontSize: 12, color: C.textoMudo }}>{t('conversas.debug_config_select')}</p>
              ) : (
                <>
                  {webhookSemSecret(debugWebhook) && (
                    <div style={{
                      padding: 10, marginBottom: 8, borderRadius: 6,
                      background: '#fef3c7', border: '1px solid #f59e0b', fontSize: 12, color: '#92400e'
                    }}>
                      <strong>⚠️ Webhook sem header de autenticação.</strong>{' '}
                      A Evolution não está enviando o header <code>x-webhook-secret</code> —
                      por isso os webhooks são rejeitados (401) antes de chegarem ao painel.
                      <div style={{ marginTop: 8 }}>
                        <button
                          onClick={reconfigurarWebhook}
                          disabled={reconfigurandoWebhook}
                          style={{
                            padding: '6px 12px', borderRadius: 5,
                            cursor: reconfigurandoWebhook ? 'not-allowed' : 'pointer',
                            background: '#f59e0b', border: 'none', color: '#fff',
                            fontSize: 12, fontWeight: 600
                          }}>
                          {reconfigurandoWebhook ? 'Reconfigurando...' : `Reconfigurar webhook de ${conversa.instancia}`}
                        </button>
                      </div>
                    </div>
                  )}
                  <pre style={{
                    background: C.bg, padding: 10, borderRadius: 4, fontSize: 11,
                    border: `1px solid ${C.border}`, overflow: 'auto', maxHeight: 200
                  }}>{JSON.stringify(debugWebhook, null, 2)}</pre>
                </>
              )}
            </div>

            <div>
              <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                {t('conversas.debug_ultimos', { n: debugLogs.length })}
              </h3>
              {debugLogs.length === 0 ? (
                <p style={{ fontSize: 12, color: C.textoMudo, padding: 12, background: '#fef3c7', borderRadius: 4 }}>
                  {t('conversas.debug_sem_webhook')}
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {debugLogs.map((log, i) => (
                    <div key={i} style={{
                      padding: 10, fontSize: 11, fontFamily: 'monospace',
                      background: log.match === 'nao_encontrada' ? '#fee2e2' : log.match === 'flexivel' ? '#fef3c7' : C.bg,
                      border: `1px solid ${C.border}`, borderRadius: 4
                    }}>
                      <div style={{ fontWeight: 700 }}>
                        {new Date(log.ts).toLocaleTimeString('pt-BR')} · {log.event}
                      </div>
                      <div>instance_recebida: <strong>"{log.instance_recebida}"</strong></div>
                      {log.instancia_normalizada && <div>→ normalizada: <strong>"{log.instancia_normalizada}"</strong> (match: {log.match})</div>}
                      {log.match === 'nao_encontrada' && (
                        <div style={{ color: '#b91c1c' }}>
                          ❌ não cadastrada. Instâncias no DB: {(log.instancias_db || []).map(n => `"${n}"`).join(', ')}
                        </div>
                      )}
                      {log.telefone && <div>tel: {log.telefone}</div>}
                      {log.texto && <div>texto: "{log.texto}"</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {quotaEsgotada && (
        <div style={{
          background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)',
          borderRadius: 10, padding: '10px 14px', margin: '0 0 10px',
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#fbbf24'
        }}>
          <span style={{ fontSize: 16 }}>⚠️</span>
          <span>
            <strong>Limite de mensagens IA atingido este mês.</strong>{' '}
            A Concierge IA está pausada para novos atendimentos.{' '}
            <a href="/creditos" style={{ color: '#f59e0b', textDecoration: 'underline', fontWeight: 600 }}>
              Adquirir Pack IA
            </a>
          </span>
        </div>
      )}

      <div style={{
        background: '#fef3c7', border: '1px solid #f59e0b',
        borderRadius: 10, padding: '12px 14px', margin: '0 0 10px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, fontSize: 13, color: '#92400e'
      }}>
        <span>
          <strong>Mensagens do WhatsApp não chegam ao painel?</strong>{' '}
          Em 24/05 ativamos autenticação do webhook por segurança. Instâncias antigas
          precisam ser re-registradas para incluir o header. Clique se a Concierge IA
          parou de responder.
        </span>
        <button
          onClick={reconfigurarTodosWebhooks}
          disabled={reconfigurandoWebhook}
          style={{
            padding: '8px 16px', borderRadius: 6, cursor: reconfigurandoWebhook ? 'not-allowed' : 'pointer',
            background: '#f59e0b', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600,
            whiteSpace: 'nowrap', flexShrink: 0
          }}
        >
          {reconfigurandoWebhook ? 'Reconfigurando...' : 'Reconfigurar todos webhooks'}
        </button>
      </div>

      {(() => {
        const pausadasCount = conversas.filter(c => c.ia_pausada_ate && new Date(c.ia_pausada_ate) > new Date()).length;
        const encerrdasCount = conversas.filter(c => c.ia_encerrada).length;
        const total = pausadasCount + encerrdasCount;
        if (total === 0) return null;
        return (
          <div style={{
            background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.35)',
            borderRadius: 10, padding: '10px 14px', margin: '0 0 10px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, fontSize: 13, color: '#dc2626'
          }}>
            <span>
              <strong>IA bloqueada em {total} conversa(s)</strong>{' '}
              {pausadasCount > 0 && `— ${pausadasCount} pausada(s) por operador`}
              {encerrdasCount > 0 && `${pausadasCount > 0 ? ', ' : '— '}${encerrdasCount} encerrada(s) por bot-detection`}.{' '}
              Clique em "Reativar todas" para liberar a Concierge IA.
            </span>
            <button
              onClick={reativarTodasConversas}
              disabled={reativandoTodas}
              style={{
                padding: '6px 14px', borderRadius: 6, cursor: reativandoTodas ? 'not-allowed' : 'pointer',
                background: '#dc2626', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600,
                whiteSpace: 'nowrap', flexShrink: 0
              }}
            >
              {reativandoTodas ? 'Reativando...' : 'Reativar todas'}
            </button>
          </div>
        );
      })()}

      <div className={`conversas-shell${conversa ? ' chat-aberto' : ''}`}>
        <div className="conversas-lista">
          <div style={{
            display: 'flex', gap: 6, padding: '10px 10px 0', borderBottom: `1px solid ${C.border}`,
            overflowX: 'auto', flexWrap: 'nowrap'
          }}>
            {[
              { id: '__todas__', label: t('conversas.qualif_todas'),     cor: C.textoSecundario, contador: conversas.length },
              { id: 'agendados', label: t('conversas.qualif_agendados'),  cor: C.ok,              contador: contadorCategoria.agendados || 0 },
              { id: 'aguardando', label: t('conversas.qualif_aguardando'), cor: C.alerta,          contador: contadorCategoria.aguardando || 0 },
            ].map(aba => {
              const ativa = filtroQualif === aba.id;
              return (
                <button
                  key={aba.id}
                  onClick={() => setFiltroQualif(aba.id)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 10px',
                    background: ativa ? aba.cor : 'transparent',
                    color: ativa ? '#fff' : C.texto,
                    border: `1px solid ${ativa ? aba.cor : C.border}`,
                    borderRadius: 999,
                    fontSize: 12, fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    marginBottom: 10
                  }}
                >
                  {aba.label}
                  {aba.contador > 0 && (
                    <span style={{
                      background: ativa ? 'rgba(255,255,255,0.25)' : C.alertaBg,
                      color: ativa ? '#fff' : C.alerta,
                      padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700
                    }}>
                      {aba.contador}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: C.textoMudo }} />
              <input
                placeholder={t('conversas.busca_placeholder')}
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                style={{ ...inputBase, paddingLeft: 32 }}
              />
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {!loading && filtradas.length === 0 && (
              <div style={{ padding: 16 }}>
                <div style={{ color: C.textoMudo }}>{t('conversas.sem_conversas')}</div>
              </div>
            )}
            {filtradas.map(c => {
              const ativa = c.id === selecionadaId;
              const pausada = pausadaAgora(c.ia_pausada_ate);
              return (
                <button
                  key={c.id}
                  onClick={() => setSelecionadaId(c.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 14px',
                    border: 'none',
                    borderBottom: `1px solid ${C.border}`,
                    borderLeft: `3px solid ${ativa ? C.primaria : 'transparent'}`,
                    background: ativa ? C.ativo : C.card,
                    cursor: 'pointer',
                    color: C.texto
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 14, color: C.texto }}>
                      {c.telefone?.includes('@g.us') ? '👥 ' : ''}{c.nome_contato || c.telefone}
                    </strong>
                    <span style={{ fontSize: 11, color: C.textoMudo }}>
                      {formatarHora(c.ultima_atividade)}
                    </span>
                  </div>
                  <div style={{
                    fontSize: 12, color: C.textoSecundario, marginTop: 4,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {c.ultima_mensagem || '—'}
                  </div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                    <span style={{
                      fontSize: 10, padding: '2px 6px', borderRadius: 4,
                      background: pausada ? C.alertaBg : C.okBg,
                      color: pausada ? C.alerta : C.ok,
                      border: `1px solid ${pausada ? C.alerta : C.ok}33`,
                      fontWeight: 600
                    }}>
                      {pausada ? t('conversas.badge_humano') : t('conversas.badge_ia_ativa')}
                    </span>
                    {c.nao_lidas > 0 && (
                      <span style={{
                        fontSize: 10, padding: '2px 7px', borderRadius: 10,
                        background: C.primaria, color: '#fff', fontWeight: 700
                      }}>
                        {c.nao_lidas}
                      </span>
                    )}
                    {c.escalada_sentimento_em && c.pendente_equipe && (
                      <span style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                        background: '#fef2f2', color: '#dc2626',
                        border: '1px solid #dc262644', fontWeight: 700
                      }}>
                        🚨 {t('conversas.badge_insatisfeito')}
                      </span>
                    )}
                    {c.pendente_equipe && (
                      <span style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                        background: C.alertaBg, color: C.alerta,
                        border: `1px solid ${C.alerta}33`, fontWeight: 700
                      }}>
                        {t('conversas.badge_pendente')}
                      </span>
                    )}
                    {c.eh_grupo_admin && (
                      <span style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                        background: c.ia_grupo_assumida ? C.okBg : C.alertaBg,
                        color: c.ia_grupo_assumida ? C.ok : C.alerta,
                        border: `1px solid ${(c.ia_grupo_assumida ? C.ok : C.alerta)}33`,
                        fontWeight: 700
                      }}>
                        {c.ia_grupo_assumida ? t('conversas.badge_ia_no_grupo') : t('conversas.badge_admin_grupo')}
                      </span>
                    )}
                    {c.followup_em && (
                      <span style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                        background: C.ativo, color: C.textoSecundario,
                        border: `1px solid ${C.border}`, fontWeight: 600
                      }}>
                        {t('conversas.badge_followup')}
                      </span>
                    )}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: C.textoSecundario }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%',
                        background: corDaInstancia(c.instancia), display: 'inline-block'
                      }} />
                      {c.instancia}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="conversas-chat">
          {!conversa ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: C.textoMudo }}>
              {t('conversas.selecione_conversa')}
            </div>
          ) : (
            <>
              <div style={{
                padding: 14, borderBottom: `1px solid ${C.border}`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button onClick={() => setSelecionadaId(null)} className="btn btn-ghost btn-sm btn-voltar-mobile">
                    {t('conversas.btn_voltar')}
                  </button>
                  <div>
                    <div style={{ fontWeight: 600, color: C.texto }}>
                      {conversa.telefone?.includes('@g.us') ? '👥 ' : ''}{conversa.nome_contato || conversa.telefone}
                    </div>
                    <div style={{ fontSize: 12, color: C.textoSecundario }}>
                      {conversa.telefone?.includes('@g.us') ? t('conversas.grupo_label') : conversa.telefone} · {conversa.instancia}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {conversa.telefone?.includes('@g.us') && conversa.eh_grupo_admin && (
                    <button
                      onClick={toggleIAGrupo}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
                        background: conversa.ia_grupo_assumida ? '#dc2626' : C.primaria,
                        border: `1px solid ${conversa.ia_grupo_assumida ? '#dc2626' : C.primaria}`,
                        color: '#fff', fontSize: 14, fontWeight: 600
                      }}
                    >
                      <Bot size={14} /> {conversa.ia_grupo_assumida ? t('conversas.btn_desativar_ia_grupo') : t('conversas.btn_ativar_ia_grupo')}
                    </button>
                  )}
                  {conversa.ia_encerrada ? (
                    <button
                      onClick={reativarIA}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
                        background: '#dc2626', border: '1px solid #dc2626',
                        color: '#fff', fontSize: 14, fontWeight: 600
                      }}
                    >
                      <Bot size={14} /> {t('conversas.btn_reativar_ia')}
                    </button>
                  ) : pausadaAgora(conversa.ia_pausada_ate) ? (
                    <button
                      onClick={retomar}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
                        background: C.card, border: `1px solid ${C.border}`,
                        color: C.texto, fontSize: 14
                      }}
                    >
                      <Bot size={14} /> {t('conversas.btn_retomar_ia')}
                    </button>
                  ) : (
                    <button
                      onClick={assumir}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
                        background: C.primaria, border: `1px solid ${C.primaria}`,
                        color: '#fff', fontSize: 14, fontWeight: 600
                      }}
                    >
                      <UserCheck size={14} /> {t('conversas.btn_assumir')}
                    </button>
                  )}
                </div>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: C.bg }}>
                {!carregandoMsgs && mensagens.length === 0 && (
                  <div style={{ color: C.textoMudo, textAlign: 'center', marginTop: 30 }}>
                    {t('conversas.sem_mensagens')}
                  </div>
                )}
                {mensagens.map(m => (
                  <div key={m.id} style={{
                    display: 'flex',
                    justifyContent: m.de_mim ? 'flex-end' : 'flex-start',
                    marginBottom: 8
                  }}>
                    <div style={{
                      maxWidth: '70%',
                      padding: '8px 12px',
                      borderRadius: 12,
                      background: m.de_mim ? C.bolhaMim : C.bolhaOutro,
                      color: m.de_mim ? '#fff' : C.texto,
                      border: m.de_mim ? 'none' : `1px solid ${C.border}`,
                      whiteSpace: 'pre-wrap',
                      fontSize: 14,
                      lineHeight: 1.4
                    }}>
                      <MidiaBolha msg={m} />
                      <div style={{
                        fontSize: 10,
                        opacity: 0.85,
                        marginTop: 4,
                        textAlign: 'right',
                        color: m.de_mim ? '#dbeafe' : C.textoMudo
                      }}>
                        {m.gerada_por_ia && <Bot size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />}
                        {formatarHora(m.ts)}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={fimRef} />
              </div>

              {anexo && (
                <div style={{
                  padding: '8px 12px', borderTop: `1px solid ${C.border}`,
                  background: '#f0f9ff', display: 'flex', alignItems: 'center', gap: 10
                }}>
                  {anexo.previewUrl
                    ? <img src={anexo.previewUrl} alt="preview" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                    : <span style={{ fontSize: 24 }}>📄</span>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{anexo.fileName}</div>
                    <div style={{ fontSize: 11, color: C.textoMudo }}>{anexo.mimetype}</div>
                  </div>
                  <button onClick={() => setAnexo(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textoMudo, padding: 4 }}>
                    <X size={16} />
                  </button>
                </div>
              )}

              <div style={{
                padding: 12, borderTop: `1px solid ${C.border}`,
                display: 'flex', gap: 8, alignItems: 'flex-end', background: C.card
              }}>
                <input type="file" ref={inputArquivoRef} onChange={selecionarArquivo}
                  accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                  style={{ display: 'none' }} />
                <button
                  onClick={() => inputArquivoRef.current?.click()}
                  title="Anexar arquivo"
                  style={{
                    background: 'none', border: `1px solid ${C.border}`, borderRadius: 6,
                    padding: '9px 10px', cursor: 'pointer', color: C.textoSecundario,
                    flexShrink: 0, display: 'inline-flex', alignItems: 'center'
                  }}
                >
                  <Paperclip size={16} />
                </button>
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={anexo ? t('conversas.placeholder_legenda') : t('conversas.placeholder_mensagem')}
                  rows={1}
                  style={{
                    ...inputBase,
                    resize: 'none',
                    minHeight: 40,
                    maxHeight: 120,
                    fontFamily: 'inherit'
                  }}
                />
                <button
                  onClick={enviar}
                  disabled={(!texto.trim() && !anexo) || enviando}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '10px 16px', borderRadius: 6,
                    cursor: ((!texto.trim() && !anexo) || enviando) ? 'not-allowed' : 'pointer',
                    background: ((!texto.trim() && !anexo) || enviando) ? C.border : C.primaria,
                    border: 'none',
                    color: '#fff', fontSize: 14, fontWeight: 600,
                    flexShrink: 0
                  }}
                >
                  <Send size={14} /> {enviando ? t('conversas.btn_enviando') : t('conversas.btn_enviar')}
                </button>
              </div>
              <div style={{
                padding: '0 12px 10px', fontSize: 11, color: C.textoMudo, background: C.card
              }}>
                {t('conversas.aviso_pausar')}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
